import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createProduct,
  fetchAllBrands,
  fetchBrandsPage,
  fetchAllCategories,
  fetchCategoriesPage,
  fetchBrandById,
  fetchCategoryById,
  fetchProductsPage,
  saveBrand,
  saveCategory,
  updateProduct,
} from "./adminApi";
import {
  normalizeBrand,
  normalizeCategory,
  normalizeProductPage,
  normalizeProductRecord,
} from "./adminTransformers";
import {
  clearPersistedAdminSnapshot,
  loadAdminSnapshot,
} from "./adminRepository";
import { createDefaultSettings } from "./mockAdminData";

const DEFAULT_PRODUCT_FILTERS = {
  brandId: "",
  categoryId: "",
  minPrice: "",
  maxPrice: "",
  minRating: "",
  maxRating: "",
  isActive: "",
  page: 0,
  size: 10,
  sortBy: "id",
  sortDir: "asc",
};

const DEFAULT_REFERENCE_FILTERS = {
  page: 0,
  size: 10,
  sortBy: "id",
  sortDir: "asc",
};

const initialState = {
  status: "idle",
  error: null,
  hydrated: false,
  products: [],
  categories: [],
  brands: [],
  orders: [],
  customers: [],
  settings: createDefaultSettings(),
  productList: {
    status: "idle",
    error: null,
    items: [],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    filters: DEFAULT_PRODUCT_FILTERS,
  },
  categoryList: {
    status: "idle",
    error: null,
    items: [],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    filters: DEFAULT_REFERENCE_FILTERS,
  },
  brandList: {
    status: "idle",
    error: null,
    items: [],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
    filters: DEFAULT_REFERENCE_FILTERS,
  },
  source: {
    mode: "idle",
    label: "Preparing workspace",
    description: "",
    apiAvailable: false,
    apiBaseUrl: "/api",
    errorMessage: "",
    hasLocalDraft: false,
    lastLoadedAt: null,
  },
  mutations: {
    saveProductStatus: "idle",
    saveCategoryStatus: "idle",
    saveBrandStatus: "idle",
    uploadStatus: "idle",
  },
};

function buildLookupMaps(state) {
  return {
    categoriesMap: new Map(state.categories.map((category) => [category.id, category])),
    brandsMap: new Map(state.brands.map((brand) => [brand.id, brand])),
  };
}

function upsertRecord(collection, item) {
  if (!item || !item.id) {
    return;
  }

  const index = collection.findIndex(
    (entry) =>
      entry.id === item.id ||
      (item.productId && entry.productId && entry.productId === item.productId)
  );

  if (index === -1) {
    collection.unshift(item);
    return;
  }

  collection[index] = item;
}

function normalizePageFilters(filters = {}) {
  return {
    ...DEFAULT_PRODUCT_FILTERS,
    ...filters,
    page: Number(filters.page ?? DEFAULT_PRODUCT_FILTERS.page) || 0,
    size: Number(filters.size ?? DEFAULT_PRODUCT_FILTERS.size) || 10,
  };
}

function normalizeReferenceFilters(filters = {}) {
  return {
    ...DEFAULT_REFERENCE_FILTERS,
    ...filters,
    page: Number(filters.page ?? DEFAULT_REFERENCE_FILTERS.page) || 0,
    size: Number(filters.size ?? DEFAULT_REFERENCE_FILTERS.size) || 10,
  };
}

export const bootstrapAdmin = createAsyncThunk(
  "admin/bootstrap",
  async (_, thunkApi) => {
    try {
      return await loadAdminSnapshot();
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to initialize the admin workspace."
      );
    }
  }
);

export const resetAdminWorkspace = createAsyncThunk(
  "admin/reset",
  async (_, thunkApi) => {
    try {
      clearPersistedAdminSnapshot();
      return await loadAdminSnapshot();
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to reset the admin workspace."
      );
    }
  }
);

export const loadProductsPage = createAsyncThunk(
  "admin/loadProductsPage",
  async (filters = {}, thunkApi) => {
    try {
      const state = thunkApi.getState().admin;
      const resolvedFilters = normalizePageFilters({
        ...state.productList.filters,
        ...filters,
      });
      const pagePayload = await fetchProductsPage(resolvedFilters);

      return {
        filters: resolvedFilters,
        pagePayload,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to load products."
      );
    }
  }
);

export const loadCategoriesPage = createAsyncThunk(
  "admin/loadCategoriesPage",
  async (filters = {}, thunkApi) => {
    try {
      const state = thunkApi.getState().admin;
      const resolvedFilters = normalizeReferenceFilters({
        ...state.categoryList.filters,
        ...filters,
      });
      const pagePayload = await fetchCategoriesPage(resolvedFilters);

      return {
        filters: resolvedFilters,
        pagePayload,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error?.message || "Unable to load categories.");
    }
  }
);

export const loadBrandsPage = createAsyncThunk(
  "admin/loadBrandsPage",
  async (filters = {}, thunkApi) => {
    try {
      const state = thunkApi.getState().admin;
      const resolvedFilters = normalizeReferenceFilters({
        ...state.brandList.filters,
        ...filters,
      });
      const pagePayload = await fetchBrandsPage(resolvedFilters);

      return {
        filters: resolvedFilters,
        pagePayload,
      };
    } catch (error) {
      return thunkApi.rejectWithValue(error?.message || "Unable to load brands.");
    }
  }
);

export const saveProductThunk = createAsyncThunk(
  "admin/saveProduct",
  async ({ productId, requestBody }, thunkApi) => {
    try {
      if (productId) {
        return {
          record: await updateProduct(productId, requestBody),
          mode: "update",
        };
      }

      return {
        record: await createProduct(requestBody),
        mode: "create",
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to save product."
      );
    }
  }
);

export const saveCategoryThunk = createAsyncThunk(
  "admin/saveCategory",
  async (payload, thunkApi) => {
    try {
      const saved = await saveCategory(payload);
      const refreshed = saved?.id ? await fetchCategoryById(saved.id) : saved;
      return normalizeCategory(refreshed || saved);
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to save category."
      );
    }
  }
);

export const saveBrandThunk = createAsyncThunk(
  "admin/saveBrand",
  async (payload, thunkApi) => {
    try {
      const saved = await saveBrand(payload);
      const refreshed = saved?.id ? await fetchBrandById(saved.id) : saved;
      return normalizeBrand(refreshed || saved);
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to save brand."
      );
    }
  }
);

export const refreshReferenceData = createAsyncThunk(
  "admin/refreshReferenceData",
  async (_, thunkApi) => {
    try {
      const [categoriesPayload, brandsPayload] = await Promise.all([
        fetchAllCategories(),
        fetchAllBrands(),
      ]);

      return {
        categories: categoriesPayload.map(normalizeCategory),
        brands: brandsPayload.map(normalizeBrand),
      };
    } catch (error) {
      return thunkApi.rejectWithValue(
        error?.message || "Unable to refresh reference data."
      );
    }
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    updateOrder: (state, action) => {
      const { orderId, changes } = action.payload;
      const order = state.orders.find((entry) => entry.id === orderId);

      if (order) {
        Object.assign(order, changes);
      }
    },
    updateCustomer: (state, action) => {
      const { customerId, changes } = action.payload;
      const customer = state.customers.find((entry) => entry.id === customerId);

      if (customer) {
        Object.assign(customer, changes);
      }
    },
    updateSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },
    clearMutationError: (state) => {
      state.error = null;
      state.productList.error = null;
      state.categoryList.error = null;
      state.brandList.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapAdmin.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(bootstrapAdmin.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.hydrated = true;
        state.products = action.payload.products || [];
        state.categories = action.payload.categories || [];
        state.brands = action.payload.brands || [];
        state.orders = action.payload.orders || [];
        state.customers = action.payload.customers || [];
        state.settings = action.payload.settings || createDefaultSettings();
        state.source = action.payload.source || state.source;
        state.categoryList.items = action.payload.categories || [];
        state.categoryList.totalElements = (action.payload.categories || []).length;
        state.categoryList.totalPages = (action.payload.categories || []).length ? 1 : 0;
        state.brandList.items = action.payload.brands || [];
        state.brandList.totalElements = (action.payload.brands || []).length;
        state.brandList.totalPages = (action.payload.brands || []).length ? 1 : 0;
      })
      .addCase(bootstrapAdmin.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error.message || "Unable to load the admin workspace.";
      })
      .addCase(resetAdminWorkspace.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(resetAdminWorkspace.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.hydrated = true;
        state.products = action.payload.products || [];
        state.categories = action.payload.categories || [];
        state.brands = action.payload.brands || [];
        state.orders = action.payload.orders || [];
        state.customers = action.payload.customers || [];
        state.settings = action.payload.settings || createDefaultSettings();
        state.source = action.payload.source || state.source;
        state.categoryList.items = action.payload.categories || [];
        state.categoryList.totalElements = (action.payload.categories || []).length;
        state.categoryList.totalPages = (action.payload.categories || []).length ? 1 : 0;
        state.brandList.items = action.payload.brands || [];
        state.brandList.totalElements = (action.payload.brands || []).length;
        state.brandList.totalPages = (action.payload.brands || []).length ? 1 : 0;
      })
      .addCase(resetAdminWorkspace.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error.message || "Unable to reset the admin workspace.";
      })
      .addCase(loadProductsPage.pending, (state) => {
        state.productList.status = "loading";
        state.productList.error = null;
      })
      .addCase(loadProductsPage.fulfilled, (state, action) => {
        const { categoriesMap, brandsMap } = buildLookupMaps(state);
        const normalizedPage = normalizeProductPage(
          action.payload.pagePayload,
          categoriesMap,
          brandsMap
        );

        state.productList = {
          ...state.productList,
          ...normalizedPage,
          status: "succeeded",
          error: null,
          filters: action.payload.filters,
        };
      })
      .addCase(loadProductsPage.rejected, (state, action) => {
        state.productList.status = "failed";
        state.productList.error =
          action.payload || action.error.message || "Unable to load products.";
      })
      .addCase(loadCategoriesPage.pending, (state) => {
        state.categoryList.status = "loading";
        state.categoryList.error = null;
      })
      .addCase(loadCategoriesPage.fulfilled, (state, action) => {
        state.categoryList = {
          ...state.categoryList,
          ...action.payload.pagePayload,
          items: action.payload.pagePayload.items.map(normalizeCategory),
          status: "succeeded",
          error: null,
          filters: action.payload.filters,
        };
      })
      .addCase(loadCategoriesPage.rejected, (state, action) => {
        state.categoryList.status = "failed";
        state.categoryList.error =
          action.payload || action.error.message || "Unable to load categories.";
      })
      .addCase(loadBrandsPage.pending, (state) => {
        state.brandList.status = "loading";
        state.brandList.error = null;
      })
      .addCase(loadBrandsPage.fulfilled, (state, action) => {
        state.brandList = {
          ...state.brandList,
          ...action.payload.pagePayload,
          items: action.payload.pagePayload.items.map(normalizeBrand),
          status: "succeeded",
          error: null,
          filters: action.payload.filters,
        };
      })
      .addCase(loadBrandsPage.rejected, (state, action) => {
        state.brandList.status = "failed";
        state.brandList.error =
          action.payload || action.error.message || "Unable to load brands.";
      })
      .addCase(saveProductThunk.pending, (state) => {
        state.mutations.saveProductStatus = "loading";
        state.error = null;
      })
      .addCase(saveProductThunk.fulfilled, (state, action) => {
        const { categoriesMap, brandsMap } = buildLookupMaps(state);
        const record = action.payload?.record;
        if (record) {
          const normalizedProduct = normalizeProductRecord(
            record,
            categoriesMap,
            brandsMap
          );

          upsertRecord(state.products, normalizedProduct);
          upsertRecord(state.productList.items, normalizedProduct);
        }
        state.mutations.saveProductStatus = "succeeded";
      })
      .addCase(saveProductThunk.rejected, (state, action) => {
        state.mutations.saveProductStatus = "failed";
        state.error =
          action.payload || action.error.message || "Unable to save product.";
      })
      .addCase(saveCategoryThunk.pending, (state) => {
        state.mutations.saveCategoryStatus = "loading";
        state.error = null;
      })
      .addCase(saveCategoryThunk.fulfilled, (state, action) => {
        upsertRecord(state.categories, action.payload);
        upsertRecord(state.categoryList.items, action.payload);
        state.products = state.products.map((product) =>
          product.categoryId === action.payload.id
            ? { ...product, categoryName: action.payload.name }
            : product
        );
        state.productList.items = state.productList.items.map((product) =>
          product.categoryId === action.payload.id
            ? { ...product, categoryName: action.payload.name }
            : product
        );
        state.mutations.saveCategoryStatus = "succeeded";
      })
      .addCase(saveCategoryThunk.rejected, (state, action) => {
        state.mutations.saveCategoryStatus = "failed";
        state.error =
          action.payload || action.error.message || "Unable to save category.";
      })
      .addCase(saveBrandThunk.pending, (state) => {
        state.mutations.saveBrandStatus = "loading";
        state.error = null;
      })
      .addCase(saveBrandThunk.fulfilled, (state, action) => {
        upsertRecord(state.brands, action.payload);
        upsertRecord(state.brandList.items, action.payload);
        state.products = state.products.map((product) =>
          product.brandId === action.payload.id
            ? { ...product, brandName: action.payload.name }
            : product
        );
        state.productList.items = state.productList.items.map((product) =>
          product.brandId === action.payload.id
            ? { ...product, brandName: action.payload.name }
            : product
        );
        state.mutations.saveBrandStatus = "succeeded";
      })
      .addCase(saveBrandThunk.rejected, (state, action) => {
        state.mutations.saveBrandStatus = "failed";
        state.error =
          action.payload || action.error.message || "Unable to save brand.";
      })
      .addCase(refreshReferenceData.fulfilled, (state, action) => {
        state.categories = action.payload.categories;
        state.brands = action.payload.brands;
      });
  },
});

export const {
  updateOrder,
  updateCustomer,
  updateSettings,
  clearMutationError,
} = adminSlice.actions;

export function selectPersistableAdminState(rootState) {
  const state = rootState.admin || rootState;

  return {
    version: 2,
    orders: state.orders,
    customers: state.customers,
    settings: state.settings,
  };
}

export default adminSlice.reducer;
